import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Card } from 'react-bootstrap';
import Button from 'components/base/Button';
import { resetPassword, ResetPasswordPayload } from 'api/authApi';
import { validatePassword } from 'utils/passwordValidation';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError(
        'Invalid or missing reset token. Please request a new password reset link.'
      );
    }
  }, [token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.new_password.trim()) {
      setError('New password is required');
      return false;
    }

    const passwordValidation = validatePassword(formData.new_password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors[0]);
      return false;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload: ResetPasswordPayload = {
        token,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password
      };

      const response = await resetPassword(payload);

      setSuccess(
        response.message ||
          'Password reset successfully! Redirecting to login...'
      );

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      console.error('Failed to reset password:', err);

      // Extract error message
      const axiosError = err as {
        response?: {
          data?: { detail?: string; message?: string };
        };
        message?: string;
      };

      const errorMessage =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'Failed to reset password. Your reset link may have expired.';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row min-vh-100 align-items-center justify-content-center">
        <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 col-xxl-4">
          <Card className="shadow-lg">
            <Card.Body className="p-4 p-sm-5">
              <div className="text-center mb-4">
                <h3 className="fw-bold">Reset Password</h3>
                <p className="text-muted">Enter your new password below</p>
              </div>

              {error && (
                <Alert variant="danger" className="mb-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success" className="mb-3">
                  <i className="fas fa-check-circle me-2"></i>
                  {success}
                </Alert>
              )}

              {!token ? (
                <div className="text-center">
                  <Alert variant="warning" className="mb-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Invalid or missing reset token
                  </Alert>
                  <Link to="/forgot-password" className="btn btn-primary">
                    Request New Reset Link
                  </Link>
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {/* New Password */}
                  <Form.Group className="mb-3">
                    <Form.Label>New Password *</Form.Label>
                    <Form.Control
                      type="password"
                      name="new_password"
                      value={formData.new_password}
                      onChange={handleInputChange}
                      placeholder="Enter your new password"
                      required
                      disabled={loading || !!success}
                      autoComplete="new-password"
                    />
                    <Form.Text className="text-muted">
                      Password must be at least 8 characters long
                    </Form.Text>
                  </Form.Group>

                  {/* Confirm Password */}
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password *</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleInputChange}
                      placeholder="Confirm your new password"
                      required
                      disabled={loading || !!success}
                      autoComplete="new-password"
                    />
                  </Form.Group>

                  <Alert variant="info" className="mb-3">
                    <i className="fas fa-info-circle me-2"></i>
                    <small>
                      <strong>Password Requirements:</strong>
                      <ul className="mb-0 mt-2">
                        <li>At least 8 characters long</li>
                        <li>At least one uppercase letter</li>
                        <li>At least one lowercase letter</li>
                        <li>At least one digit</li>
                        <li>Both password fields must match</li>
                      </ul>
                    </small>
                  </Alert>

                  <div className="d-grid mb-3">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading || !!success}
                      className="py-2"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Resetting Password...
                        </>
                      ) : (
                        'Reset Password'
                      )}
                    </Button>
                  </div>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="text-decoration-none"
                      onClick={e => {
                        if (loading) e.preventDefault();
                      }}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to Login
                    </Link>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
